import { pvProductConfig, PV_DEFAULTS, PV_SYSTEM_EVAL_ORDER } from '../config/pv-product-config';
import { getDisplayName, BATTERY_MODULE_BRUTTO_KWH } from '../config/general-product-config';
import { orientationFactors } from '../config/defaults';

// ============= Helper Functions =============

export function calculateColdVoc(vocPerModule, betaVocPercent, minDesignTempC) {
  const beta = betaVocPercent || PV_DEFAULTS.betaVocPercent;
  const tMin = minDesignTempC ?? PV_DEFAULTS.minDesignTempC;
  return vocPerModule * (1 + Math.abs(beta) / 100 * (PV_DEFAULTS.stcTempC - tMin));
}

export function calculateEstimatedVmp(vocPerModule, vmpPerModule) {
  return (vmpPerModule && vmpPerModule > 0) ? vmpPerModule : vocPerModule * PV_DEFAULTS.vmpEstimateFactor;
}

export function calculateStringPower(moduleCount, powerWpPerModule) {
  return (moduleCount * powerWpPerModule) / 1000;
}

export function calculateTotalPvPower(entries) {
  return entries.reduce((sum, e) => sum + (e.moduleCount * e.powerWpPerModule) / 1000, 0);
}

// Orientation factor from dropdown value
export function getOrientationFactor(orientation) {
  if (orientation && orientationFactors[orientation] !== undefined) {
    return orientationFactors[orientation];
  }
  return 1.0;
}

// Check if two strings can be paralleled on the same MPPT
export function canParallelStrings(s1, s2) {
  return s1.moduleType === s2.moduleType && s1.moduleCount === s2.moduleCount &&
    Math.abs(s1.vocPerModule - s2.vocPerModule) < 0.01 &&
    Math.abs(s1.powerWpPerModule - s2.powerWpPerModule) < 0.01 &&
    Math.abs(s1.nominalCurrentPerModule - s2.nominalCurrentPerModule) < 0.01;
}

// ============= Exhaustive Tracker Assignment =============
// Uses backtracking to find ANY valid assignment of all strings to trackers.
// Group limits use effective (orientation-discounted) power.
// Per-tracker limits use NOMINAL power (DC hardware limit, peak STC).

function checkGroupLimits(trackerStates, system) {
  if (!system.groupLimits) return true;
  for (const group of system.groupLimits) {
    const groupPower = group.trackerIds.reduce((sum, tid) => {
      const t = trackerStates.find(tr => tr.id === tid);
      // Gruppen-Limit ist ebenfalls ein DC-Hardware-Limit → nominal
      return sum + (t ? t.nominalPowerKw : 0);
    }, 0);
    if (groupPower > group.maxPvKw + 0.01) return false;
  }
  return true;
}

function getCompatibleTrackerIds(str, system) {
  const coldVoc = str.stringVocCold || (str.moduleCount * calculateColdVoc(str.vocPerModule, str.betaVocPercent, str.minDesignTempC));
  const vmpMod = calculateEstimatedVmp(str.vocPerModule, str.vmpPerModule);
  const stringVmp = str.moduleCount * vmpMod;
  // Sommer-Vmp: bei +70°C Modultemperatur fällt Vmp gegenüber STC um ca. 15-18 %
  // (βVmp ≈ -0,35 %/K × (70-25)K). Die MPPT-Mindestspannung muss auch bei
  // Hochsommer-Betrieb noch eingehalten werden → wir rechnen stringVmp * 0.82
  // als konservative Heiß-Schätzung gegen t.minV.
  const stringVmpHot = stringVmp * 0.82;
  const stringCurrent = str.nominalCurrentPerModule;
  return system.mppts
    .filter(t => stringVmpHot >= t.minV && coldVoc <= t.maxV && stringCurrent <= t.maxA)
    .map(t => t.id);
}

function tryExhaustiveAssignment(strings, system) {
  const n = strings.length;
  if (n === 0) return null;

  // 04.02.2026 — Defensive: Tracker mit fehlender/kaputter `maxPvKw`
  // konnten bisher zu Überladung führen (JS-Vergleich `x > undefined+0.01`
  // → NaN → false → keine Zurückweisung, Tracker sammelte >maxPvKw ohne
  // Warnung). Jetzt: einen normalisierten View auf `system.mppts` bauen,
  // damit weiter unten der Kapazitätscheck deterministisch greift und
  // `getCompatibleTrackerIds` denselben View sieht.
  const mpptsSafe = (system.mppts || []).map(t => ({
    ...t,
    maxPvKw: (typeof t.maxPvKw === 'number' && isFinite(t.maxPvKw) && t.maxPvKw > 0)
      ? t.maxPvKw
      : 0, // 0 = Tracker unbenutzbar → jede Zuweisung wird verworfen
    maxA: (typeof t.maxA === 'number' && isFinite(t.maxA) && t.maxA > 0)
      ? t.maxA
      : Infinity, // maxA ist optional strenger — nicht blockieren, wenn fehlt
  }));
  const systemSafe = { ...system, mppts: mpptsSafe };

  const compatMap = strings.map(str => {
    const compat = getCompatibleTrackerIds(str, systemSafe);
    const preferred = systemSafe.preferredPrimaryTrackers || [];
    const secondary = systemSafe.secondaryTrackers || [];
    return [
      ...preferred.filter(id => compat.includes(id)),
      ...secondary.filter(id => compat.includes(id)),
      ...compat.filter(id => !preferred.includes(id) && !secondary.includes(id))
    ];
  });

  if (compatMap.some(c => c.length === 0)) return null;

  // MCV heuristic: sort strings by number of compatible trackers (ascending),
  // then by orientation factor (descending — highest yield first to claim primary trackers),
  // then by power (descending) as tiebreaker.
  const indices = strings.map((_, i) => i);
  indices.sort((a, b) => {
    const diff = compatMap[a].length - compatMap[b].length;
    if (diff !== 0) return diff;
    const factorA = getOrientationFactor(strings[a].orientation);
    const factorB = getOrientationFactor(strings[b].orientation);
    if (Math.abs(factorA - factorB) > 0.05) return factorB - factorA;
    return strings[b].powerKw - strings[a].powerKw;
  });
  const sorted = indices.map(i => strings[i]);
  const sortedCompat = indices.map(i => compatMap[i]);

  let bestResult = null;

  function search(idx, state) {
    if (idx === n) {
      bestResult = state.map(t => ({
        id: t.id, strings: [...t.strings],
        nominalPowerKw: t.nominalPowerKw, effectivePowerKw: t.effectivePowerKw, currentA: t.currentA,
      }));
      return true;
    }
    const str = sorted[idx];
    for (const tId of sortedCompat[idx]) {
      const tIdx = systemSafe.mppts.findIndex(t => t.id === tId);
      const tracker = state[tIdx];
      const mppt = systemSafe.mppts[tIdx];
      if (tracker.strings.length === 0) {
        // 04.02.2026 — Tracker-Kapazität ist ein DC-seitiges Hardware-Limit
        // (peak STC-Leistung), NICHT orientierungs-derated. Ein 9-Modul-
        // West-String liefert am Sonnenmittag 9×450 Wp=4.05 kW physikalisch
        // → würde einen 3.6-kW-Tracker überladen, egal ob Jahres-Ertrag nur
        // 83 % beträgt. Deshalb hier `nominalPowerKw`/`powerKw` statt
        // `effectivePowerKw` für den maxPvKw-Check.
        if (tracker.nominalPowerKw + str.powerKw > mppt.maxPvKw + 0.01) continue;
        tracker.strings.push(str);
        tracker.nominalPowerKw += str.powerKw;
        tracker.effectivePowerKw += str.effectivePowerKw;
        tracker.currentA += str.nominalCurrentPerModule;
        if (checkGroupLimits(state, systemSafe)) { if (search(idx + 1, state)) return true; }
        tracker.strings.pop();
        tracker.nominalPowerKw -= str.powerKw;
        tracker.effectivePowerKw -= str.effectivePowerKw;
        tracker.currentA -= str.nominalCurrentPerModule;
      } else if (tracker.strings.length < PV_DEFAULTS.maxParallelPerMppt) {
        if (!canParallelStrings(tracker.strings[0], str)) continue;
        const cc = tracker.currentA + str.nominalCurrentPerModule;
        if (cc > mppt.maxA + 0.01) continue;
        // s. o. — DC-Leistungslimit auf NOMINAL prüfen
        if (tracker.nominalPowerKw + str.powerKw > mppt.maxPvKw + 0.01) continue;
        tracker.strings.push(str);
        tracker.nominalPowerKw += str.powerKw;
        tracker.effectivePowerKw += str.effectivePowerKw;
        tracker.currentA = cc;
        if (checkGroupLimits(state, systemSafe)) { if (search(idx + 1, state)) return true; }
        tracker.strings.pop();
        tracker.nominalPowerKw -= str.powerKw;
        tracker.effectivePowerKw -= str.effectivePowerKw;
        tracker.currentA = cc - str.nominalCurrentPerModule;
      }
    }
    return false;
  }

  const initialState = systemSafe.mppts.map(t => ({
    id: t.id, strings: [], nominalPowerKw: 0, effectivePowerKw: 0, currentA: 0,
  }));
  search(0, initialState);
  return bestResult;
}

// ============= Field Mode: Split Generation =============

function generateFieldSplits(moduleCount, minMod, maxMod, maxStrings) {
  const results = [];
  const effectiveMin = Math.max(minMod, 3);
  const effectiveMax = Math.min(maxMod, moduleCount);
  if (effectiveMin > effectiveMax) return [];

  function bt(remaining, current) {
    if (remaining === 0) { results.push([...current]); return; }
    if (current.length >= maxStrings || results.length > 200) return;
    const maxN = Math.min(effectiveMax, remaining);
    for (let len = maxN; len >= effectiveMin; len--) {
      if (current.length > 0 && len > current[current.length - 1]) continue;
      if (remaining - len > 0 && remaining - len < effectiveMin && current.length === maxStrings - 1) continue;
      current.push(len);
      bt(remaining - len, current);
      current.pop();
    }
  }
  bt(moduleCount, []);
  // Sort by: 1) string count ascending, 2) BALANCE (min spread) first within same count
  return results.sort((a, b) => {
    if (a.length !== b.length) return a.length - b.length;
    const spreadA = Math.max(...a) - Math.min(...a);
    const spreadB = Math.max(...b) - Math.min(...b);
    return spreadA - spreadB;
  });
}

function getTrackerRangesForField(field, system) {
  const coldVoc = calculateColdVoc(field.vocPerModule, field.betaVocPercent, field.minDesignTempC);
  const vmpMod = calculateEstimatedVmp(field.vocPerModule, field.vmpPerModule);
  const pwPerMod = field.powerWpPerModule / 1000;
  const orientFactor = getOrientationFactor(field.orientation);
  const effectivePwPerMod = pwPerMod * orientFactor;

  return system.mppts.map(t => ({
    id: t.id,
    // Hot-Vmp derate (0.82) bei Mindest-Modulzahl berücksichtigen
    minMod: Math.max(3, Math.ceil(t.minV / (vmpMod * 0.82))),
    // 04.02.2026 — Hardware-Limit ist DC-seitig NOMINAL (peak STC-Power),
    // NICHT orientierungs-derated. Ein 9-Modul-West-String liefert am
    // Sonnenmittag physikalisch 9 × 450 Wp = 4.05 kW → würde einen 3.6-kW-
    // Tracker überladen, selbst wenn der Jahres-Ertrag nur 83 % beträgt.
    // Deshalb hier `pwPerMod` statt `effectivePwPerMod`.
    maxMod: Math.min(Math.floor(t.maxV / coldVoc), Math.floor(t.maxPvKw / pwPerMod)),
  })).filter(r => r.maxMod >= r.minMod);
}

function generateSystemSpecificFieldSplits(field, system) {
  const ranges = getTrackerRangesForField(field, system);
  if (ranges.length === 0) return [];
  const globalMin = Math.min(...ranges.map(r => r.minMod));
  const globalMax = Math.max(...ranges.map(r => r.maxMod));
  const maxStringsForSystem = Math.min(PV_DEFAULTS.maxStringsInConfigurator, system.mppts.length * PV_DEFAULTS.maxParallelPerMppt);
  return generateFieldSplits(field.moduleCount, globalMin, globalMax, maxStringsForSystem);
}

function buildStringFromField(field, fieldIdx, moduleCount, globalIdx) {
  const coldVoc = calculateColdVoc(field.vocPerModule, field.betaVocPercent, field.minDesignTempC);
  const vmpMod = calculateEstimatedVmp(field.vocPerModule, field.vmpPerModule);
  const powerKw = calculateStringPower(moduleCount, field.powerWpPerModule);
  const orientFactor = getOrientationFactor(field.orientation);
  return {
    stringName: `${field.fieldName || `Feld ${fieldIdx + 1}`} - String ${globalIdx}`,
    displayName: `${field.fieldName || `Feld ${fieldIdx + 1}`} - String ${globalIdx}`,
    moduleType: field.moduleType, moduleCount,
    vocPerModule: field.vocPerModule, powerWpPerModule: field.powerWpPerModule,
    nominalCurrentPerModule: field.nominalCurrentPerModule,
    betaVocPercent: field.betaVocPercent, minDesignTempC: field.minDesignTempC,
    vmpPerModule: field.vmpPerModule,
    coldVocPerModule: coldVoc, stringVocCold: moduleCount * coldVoc,
    stringVmpEstimate: moduleCount * vmpMod,
    powerKw,
    effectivePowerKw: powerKw * orientFactor,
    orientation: field.orientation,
    stringCurrentA: field.nominalCurrentPerModule,
  };
}

function generateCombinations(fieldSplitOptions, fields, maxTotalStrings) {
  const results = [];
  function recurse(fieldIdx, currentStrings, currentGlobalIdx) {
    if (fieldIdx >= fieldSplitOptions.length) {
      if (currentStrings.length <= maxTotalStrings) results.push([...currentStrings]);
      return;
    }
    if (results.length > 500) return;
    for (const split of fieldSplitOptions[fieldIdx]) {
      if (currentStrings.length + split.length > maxTotalStrings) continue;
      const savedIdx = currentGlobalIdx;
      const newStrings = split.map((mc) => {
        currentGlobalIdx++;
        return buildStringFromField(fields[fieldIdx], fieldIdx, mc, currentGlobalIdx);
      });
      recurse(fieldIdx + 1, [...currentStrings, ...newStrings], currentGlobalIdx);
      currentGlobalIdx = savedIdx;
    }
  }
  recurse(0, [], 0);
  // Don't just sort by fewest strings — interleave different string counts
  // so the scorer can evaluate both compact and spread-out variants
  results.sort((a, b) => {
    // Group by total string count, but within each group prefer balanced splits
    if (a.length !== b.length) return a.length - b.length;
    // Within same count: prefer more equal module distribution
    const spreadA = Math.max(...a.map(s => s.powerKw)) - Math.min(...a.map(s => s.powerKw));
    const spreadB = Math.max(...b.map(s => s.powerKw)) - Math.min(...b.map(s => s.powerKw));
    return spreadA - spreadB;
  });
  return results;
}

// ============= MAIN: Find Best PV System Solutions =============
// Evaluates systems in fixed order (smallest first).
// First match = Recommendation, second match = Alternative.
// If recommendation is HS 15 PV duo, no alternative.

export function findBestPvSystemSolutions(entries, mode, annualConsumptionKwh, recommendedModelOverride = null) {
  const totalPvKw = calculateTotalPvPower(entries);
  if (totalPvKw === 0) return { solutions: [], totalPvKw, warnings: ["Keine PV-Leistung eingegeben."] };

  // Calculate effective total PV power (with orientation discounts)
  const effectivePvKw = entries.reduce((sum, e) => {
    const factor = getOrientationFactor(e.orientation || e.fieldOrientation);
    return sum + (e.moduleCount * e.powerWpPerModule) / 1000 * factor;
  }, 0);

  const warnings = [];
  const solutions = [];

  for (const modelName of PV_SYSTEM_EVAL_ORDER) {
    if (solutions.length >= 2) break;

    const system = pvProductConfig.find(s => s.model === modelName);
    if (!system) continue;

    // Filter: effectivePvKw must be within 120% of recommendedPvKw
    if (effectivePvKw > system.recommendedPvKw * (1 + PV_DEFAULTS.pvToleranceFilter)) continue;

    let assignment = null;
    let vmpEstimated = false;

    if (mode === 'strings') {
      const strings = entries.map((e, idx) => {
        const coldVoc = calculateColdVoc(e.vocPerModule, e.betaVocPercent, e.minDesignTempC);
        const vmpMod = calculateEstimatedVmp(e.vocPerModule, e.vmpPerModule);
        if (!e.vmpPerModule) vmpEstimated = true;
        const orientFactor = getOrientationFactor(e.orientation);
        const powerKw = calculateStringPower(e.moduleCount, e.powerWpPerModule);
        return {
          ...e, coldVocPerModule: coldVoc, stringVocCold: e.moduleCount * coldVoc,
          stringVmpEstimate: e.moduleCount * vmpMod,
          powerKw, effectivePowerKw: powerKw * orientFactor,
          stringCurrentA: e.nominalCurrentPerModule,
          displayName: e.stringName || `String ${idx + 1}`,
        };
      });
      assignment = tryExhaustiveAssignment(strings, system);

    } else {
      // Field mode: generate splits per field, try combinations
      // Score each valid assignment by primary tracker utilization
      const fieldSplitOptions = entries.map(field => generateSystemSpecificFieldSplits(field, system));
      if (fieldSplitOptions.some(opts => opts.length === 0)) continue;

      // Select representative splits: top N per string-count group to ensure diversity
      const trimmedOptions = fieldSplitOptions.map(opts => {
        const byCount = {};
        for (const s of opts) {
          const k = s.length;
          if (!byCount[k]) byCount[k] = [];
          byCount[k].push(s);
        }
        const selected = [];
        for (const k of Object.keys(byCount).sort((a, b) => a - b)) {
          // Take top 5 from each string-count group (sorted by spread asc)
          selected.push(...byCount[k].slice(0, 5));
        }
        return selected;
      });
      const combinations = generateCombinations(trimmedOptions, entries, PV_DEFAULTS.maxStringsInConfigurator);

      let bestAssignment = null;
      let bestScore = -Infinity;
      let bestVmpEstimated = false;

      for (const combo of combinations.slice(0, 300)) {
        const result = tryExhaustiveAssignment(combo, system);
        if (result) {
          const primaryIds = system.preferredPrimaryTrackers || [];
          const primaryTrackers = result.filter(t => primaryIds.includes(t.id) && t.strings.length > 0);
          const totalUsed = result.filter(t => t.strings.length > 0).length;

          const primaryCount = primaryTrackers.length;
          const secondaryIds = system.secondaryTrackers || [];
          const secondaryStringCount = result.filter(t => secondaryIds.includes(t.id)).reduce((sum, t) => sum + t.strings.length, 0);
          let score = primaryCount * 80 - combo.length * 3 - secondaryStringCount * 10;

          // Yield-based scoring: place highest-yield strings on primary trackers
          const allFactors = combo.map(s => getOrientationFactor(s.orientation));
          const maxFactor = Math.max(...allFactors);
          const hasMixedYields = maxFactor - Math.min(...allFactors) > 0.1;
          if (hasMixedYields) {
            for (const t of result) {
              if (t.strings.length === 0) continue;
              const isPrimary = primaryIds.includes(t.id);
              for (const s of t.strings) {
                const factor = getOrientationFactor(s.orientation);
                if (isPrimary) {
                  score += factor >= maxFactor - 0.05 ? s.powerKw * 20 : -s.powerKw * 15;
                }
              }
            }
          }

          // Primary tracker SATURATION bonus: reward loading primary trackers close to their maxPvKw
          for (const t of primaryTrackers) {
            const mppt = system.mppts.find(m => m.id === t.id);
            if (mppt && mppt.maxPvKw > 0) {
              // Saturation nach NOMINAL-Leistung (DC-Hardware-Auslastung),
              // konsistent mit den maxPvKw-Checks im Sizer.
              const saturation = t.nominalPowerKw / mppt.maxPvKw;
              score += saturation * 50;
            }
          }

          if (score > bestScore) {
            bestScore = score;
            bestAssignment = result;
            bestVmpEstimated = combo.some(s => !s.vmpPerModule);
          }
        }
      }
      if (bestAssignment) {
        assignment = bestAssignment;
        vmpEstimated = bestVmpEstimated;
      }
    }

    if (assignment) {
      // Convert to UI-expected format
      const assigned = [];
      const trackers = assignment.map(t => {
        const mppt = system.mppts.find(m => m.id === t.id);
        t.strings.forEach(s => assigned.push({ ...s, trackerId: t.id, parallel: t.strings.length > 1 }));
        return { ...mppt, assigned: t.strings, totalPowerKw: t.nominalPowerKw, totalCurrentA: t.currentA };
      });

      const isLastSystem = modelName === 'HS 15 PV duo';
      const isRecommendation = solutions.length === 0;

      solutions.push({
        system: { ...system, displayName: getDisplayName(system.model) },
        assignment: { assigned, unassigned: [], trackers },
        totalPvKw,
        vmpEstimated,
        reason: isRecommendation
          ? 'Kleinstes geeignetes System für Ihre PV-Konfiguration'
          : 'Nächstgrößere Alternative',
      });

      if (isRecommendation && isLastSystem) {
        warnings.push('Bei höherer Dimensionierung wenden Sie sich bitte an order@ees-energiespeicher.de für eine individuelle Lösung.');
        break;
      }
    }
  }

  if (solutions.length === 0) {
    // If no system matched, recommend largest and add special note
    const lastSystem = pvProductConfig.find(s => s.model === PV_SYSTEM_EVAL_ORDER[PV_SYSTEM_EVAL_ORDER.length - 1]);
    if (lastSystem) {
      warnings.push(`Die PV-Leistung von ${totalPvKw.toFixed(1)} kWp übersteigt die verfügbaren Standardsysteme. Das größtmögliche System wird empfohlen. Für eine Sonderlösung wenden Sie sich bitte an order@ees-energiespeicher.de.`);
      solutions.push({
        system: { ...lastSystem, displayName: getDisplayName(lastSystem.model) },
        assignment: { assigned: [], unassigned: [], trackers: [] },
        totalPvKw, vmpEstimated: false,
        reason: 'Größtmögliches verfügbares System (Sonderlösung erforderlich)',
      });
    }
  }

  // If recommendation is the largest system in its family, no alternative
  if (solutions.length >= 2) {
    const recModel = solutions[0].system.model;
    const lastPrivateModel = PV_SYSTEM_EVAL_ORDER[PV_SYSTEM_EVAL_ORDER.length - 1];
    if (recModel === lastPrivateModel) {
      solutions.splice(1);
      warnings.push('Die Systemempfehlung ist bereits das größtmögliche System der Produktfamilie. Eine sinnvolle Alternative kann daher nicht aufgezeigt werden.');
    }
  }

  // Downsize alternative: if it uses fewer trackers than available, check if a simpler system works
  if (solutions.length >= 2) {
    const alt = solutions[1];
    const altSys = alt.system;
    const usedTrackerCount = alt.assignment.trackers.filter(t => t.assigned?.length > 0).length;
    if (usedTrackerCount < altSys.mppts.length) {
      // Try all systems that were skipped by the tolerance filter but have enough trackers
      const altStrings = alt.assignment.assigned || [];
      if (altStrings.length > 0) {
        const recModel = solutions[0].system.model;
        for (const modelName of PV_SYSTEM_EVAL_ORDER) {
          if (modelName === recModel || modelName === altSys.model) continue;
          const candidate = pvProductConfig.find(s => s.model === modelName);
          if (!candidate || candidate.mppts.length > usedTrackerCount) continue;
          // Don't downsize to something smaller than the recommendation
          const recIdx = PV_SYSTEM_EVAL_ORDER.indexOf(recModel);
          const candIdx = PV_SYSTEM_EVAL_ORDER.indexOf(modelName);
          if (candIdx <= recIdx) continue;
          const testResult = tryExhaustiveAssignment(altStrings, candidate);
          if (testResult) {
            const assigned = [];
            const trackers = testResult.map(t => {
              const mppt = candidate.mppts.find(mp => mp.id === t.id);
              t.strings.forEach(s => assigned.push({ ...s, trackerId: t.id, parallel: t.strings.length > 1 }));
              return { ...mppt, assigned: t.strings, totalPowerKw: t.nominalPowerKw, totalCurrentA: t.currentA };
            });
            solutions[1] = {
              ...alt,
              system: { ...candidate, displayName: getDisplayName(candidate.model) },
              assignment: { assigned, unassigned: [], trackers },
              reason: 'Nächstgrößere Alternative',
            };
            break;
          }
        }
      }
    }
  }

  if (entries.length > PV_DEFAULTS.maxStringsInConfigurator) {
    warnings.push(`Es können bis zu ${PV_DEFAULTS.maxStringsInConfigurator} Strings im Konfigurator geprüft werden.`);
  }

  // Post-processing: annual consumption check for battery sizing AND system upsizing
  const consumption = annualConsumptionKwh ? Number(annualConsumptionKwh) : 0;
  if (consumption > 0 && solutions.length > 0) {
    const annualPvYield = totalPvKw * PV_DEFAULTS.pvYieldPerKwp;
    const dailyConsumption = consumption / 365;
    const dailyPvYield = annualPvYield / 365;
    const directShareEstimate = Math.min(0.35, dailyConsumption > 0 ? 0.25 : 0);
    const dailySurplus = dailyPvYield * (1 - directShareEstimate);
    const targetEnergyKwh = Math.min(dailyConsumption * 0.4, dailySurplus);
    const cappedTargetEnergy = Math.max(targetEnergyKwh, 4.864);

    // Estimate required discharge power from consumption
    const avgPowerKw = consumption / 8760;
    const targetPowerKw = Math.max(avgPowerKw * 5.5, 3);

    for (let i = 0; i < solutions.length; i++) {
      const sol = solutions[i];
      const sys = sol.system;

      // Check if system is too small for power needs
      if (targetPowerKw > sys.maxDischargeKw * 1.3) {
        // Find the next larger system that still fits the strings
        const currentIdx = PV_SYSTEM_EVAL_ORDER.indexOf(sys.model);
        let upgraded = false;
        for (let j = currentIdx + 1; j < PV_SYSTEM_EVAL_ORDER.length; j++) {
          const biggerModel = PV_SYSTEM_EVAL_ORDER[j];
          const biggerSys = pvProductConfig.find(s => s.model === biggerModel);
          if (!biggerSys) continue;
          if (biggerSys.maxDischargeKw >= targetPowerKw * 0.7) {
            // Try to assign existing strings to the bigger system
            const existingStrings = sol.assignment.assigned || [];
            if (existingStrings.length > 0) {
              const testResult = tryExhaustiveAssignment(existingStrings, biggerSys);
              if (testResult) {
                const assigned = [];
                const trackers = testResult.map(t => {
                  const mppt = biggerSys.mppts.find(mp => mp.id === t.id);
                  t.strings.forEach(s => assigned.push({ ...s, trackerId: t.id, parallel: t.strings.length > 1 }));
                  return { ...mppt, assigned: t.strings, totalPowerKw: t.nominalPowerKw, totalCurrentA: t.currentA };
                });
                solutions[i] = {
                  ...sol,
                  system: { ...biggerSys, displayName: getDisplayName(biggerSys.model) },
                  assignment: { assigned, unassigned: [], trackers },
                  reason: i === 0
                    ? 'Auf Basis Ihres Jahresverbrauchs wurde ein leistungsstärkeres System gewählt'
                    : 'Nächstgrößere Alternative auf Basis Ihres Jahresverbrauchs',
                };
                upgraded = true;
                break;
              }
            } else {
              // No string assignment (fallback) — just upgrade the system reference
              solutions[i] = {
                ...sol,
                system: { ...biggerSys, displayName: getDisplayName(biggerSys.model) },
                reason: i === 0
                  ? 'Auf Basis Ihres Jahresverbrauchs wurde ein leistungsstärkeres System gewählt'
                  : 'Nächstgrößere Alternative auf Basis Ihres Jahresverbrauchs',
              };
              upgraded = true;
              break;
            }
          }
        }
        if (!upgraded) {
          warnings.push(`Der Jahresverbrauch von ${consumption.toLocaleString('de-DE')} kWh erfordert möglicherweise ein leistungsstärkeres System als verfügbar. Bitte kontaktieren Sie order@ees-energiespeicher.de.`);
        }
      }

      // Battery sizing for the (possibly upgraded) system
      const finalSys = solutions[i].system;
      if (cappedTargetEnergy > finalSys.standardUsableKwh) {
        const additionalModules = Math.ceil((cappedTargetEnergy - finalSys.standardUsableKwh) / BATTERY_MODULE_BRUTTO_KWH);
        solutions[i].additionalBattery = { modules: additionalModules, bruttoKwh: additionalModules * BATTERY_MODULE_BRUTTO_KWH };
        solutions[i].targetEnergyKwh = Math.round(cappedTargetEnergy * 100) / 100;
      }
    }

    // After upsizing, ensure recommendation and alternative are different systems
    if (solutions.length >= 2 && solutions[0].system.model === solutions[1].system.model) {
      solutions.splice(1);
    }
  }

  // Frag-HANS-Override: wenn HANS bereits ein konkretes Modell empfohlen
  // hat (z. B. nach Generator-Folgefrage HS 6 PV → HS 9 PV), muss die
  // PV-Konfigurator-Primärempfehlung dasselbe Modell zeigen — sonst Desync
  // zwischen Chat und Detail-Seite. Override greift nur, wenn das Modell
  // existiert und Strings/Felder zugeordnet werden können.
  if (recommendedModelOverride && solutions.length > 0
      && solutions[0].system.model.toLowerCase() !== recommendedModelOverride.toLowerCase()) {
    const overrideSys = pvProductConfig.find(
      (s) => (s.model || '').toLowerCase() === recommendedModelOverride.toLowerCase()
    );
    if (overrideSys) {
      // Strings aus der bisherigen Primärempfehlung wiederverwenden (sie sind
      // bereits in der von `tryExhaustiveAssignment` erwarteten Form), sonst
      // aus den Entries neu bauen.
      let overrideStrings = solutions[0].assignment.assigned || [];
      if (overrideStrings.length === 0) {
        overrideStrings = entries.map((e, idx) => {
          const coldVoc = calculateColdVoc(e.vocPerModule, e.betaVocPercent, e.minDesignTempC);
          const vmpMod = calculateEstimatedVmp(e.vocPerModule, e.vmpPerModule);
          const orientFactor = getOrientationFactor(e.orientation);
          const powerKw = calculateStringPower(e.moduleCount, e.powerWpPerModule);
          return {
            ...e, coldVocPerModule: coldVoc, stringVocCold: e.moduleCount * coldVoc,
            stringVmpEstimate: e.moduleCount * vmpMod,
            powerKw, effectivePowerKw: powerKw * orientFactor,
            stringCurrentA: e.nominalCurrentPerModule,
            displayName: e.stringName || e.fieldName || `String ${idx + 1}`,
          };
        });
      }
      const testResult = tryExhaustiveAssignment(overrideStrings, overrideSys);
      if (testResult) {
        const assigned = [];
        const trackers = testResult.map(t => {
          const mppt = overrideSys.mppts.find(mp => mp.id === t.id);
          t.strings.forEach(s => assigned.push({ ...s, trackerId: t.id, parallel: t.strings.length > 1 }));
          return { ...mppt, assigned: t.strings, totalPowerKw: t.nominalPowerKw, totalCurrentA: t.currentA };
        });
        const previousPrimary = solutions[0];
        const overrideSolution = {
          system: { ...overrideSys, displayName: getDisplayName(overrideSys.model) },
          assignment: { assigned, unassigned: [], trackers },
          totalPvKw,
          vmpEstimated: previousPrimary.vmpEstimated || false,
          reason: 'Empfehlung aus Frag-HANS-Dialog',
        };
        // Bisherige Primärempfehlung wird zur Alternative (sofern unterschiedlich)
        const newAlt = (previousPrimary.system.model.toLowerCase() !== overrideSys.model.toLowerCase())
          ? { ...previousPrimary, reason: 'Kleinere Alternative aus PV-Auslegung' }
          : (solutions[1] || null);
        solutions.length = 0;
        solutions.push(overrideSolution);
        if (newAlt) solutions.push(newAlt);
      }
    }
  }

  return { solutions: solutions.slice(0, 2), totalPvKw: Math.round(totalPvKw * 100) / 100, warnings };
}
